import React from 'react';
import { Card, CardContent, Typography, Box, useMediaQuery, useTheme } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

function PieChartCard({ data }) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasData = data && Object.keys(data).length > 0;

  const chartData = hasData 
    ? Object.entries(data).map(([name, value]) => ({
        name,
        value,
        color: getColorForIndex(Object.keys(data).indexOf(name))
      }))
    : [];

  function getColorForIndex(index) {
    const colors = ['#00B894', '#0081a7', '#00b4d8', '#90e0ef', '#f07167', '#fed9b7'];
    return colors[index % colors.length];
  }

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Card sx={{ 
      width: isMobile ? '100%' : isTablet ? '100%' : '100%',
      height: isTablet ? 'auto' : '400px',
      borderRadius: '16px', 
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
      mt: isTablet ? 2 : 0
    }}>
      <CardContent sx={{ height: '100%', p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#00B894', mb: 2 }}>
          Donation Distribution
        </Typography>
        <Box sx={{ 
          height: isTablet ? '300px' : '320px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {hasData ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  borderRadius={100}
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={isTablet ? 80 : 100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #00B894',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend 
                  layout={isTablet ? 'horizontal' : 'vertical'} 
                  verticalAlign={isTablet ? 'bottom' : 'middle'}
                  align={isTablet ? 'center' : 'right'}
                  wrapperStyle={isTablet ? { paddingTop: '20px' } : {}}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                textAlign: 'center',
                fontStyle: 'italic'
              }}
            >
              No data available
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default PieChartCard;